(ns app.domain.group.integration-config-model
  (:require [app.domain.group.config.integration-model :as integration-model]
            [core.db.child-entity :as child]
            [core.domain.schemas :as schemas]
            [schema-tools.core :as st]
            [schema.core :as s]))

(def admin-only-services
  "Integration types that can only be enabled by Cloudpermit admins, e.g. due to requiring
   configuration elsewhere (siirappi, firewalls etc)"
  #{;; the exchange integration can only be configured by CloudPermi admins, because it needs
    ;; configuration in the siirappi config file
    :integration-config.service/exchange})

(def laserfiche-common
  (st/merge {:integration-config/package-pdf-template (s/cond-pre s/Int s/Str)}
            (st/optional-keys
              ;; send ws automatically if it's finished e.g. via issuance "pipeline" and permit issued is the final state
              {:integration-config/ecm-send-auto-finished? s/Bool
               :integration-config/workspace-attr-mapping  integration-model/IntegrationWorkspaceAttrMapping})))

(def integration-configs-by-service
  "type -> service -> config keys
  Note that the order of keys here is directly used in UI rendering, so these must be ArrayMaps.
  If you have more than 8 config options, you probably have to create the map explicitly as ArrayMap."
  {:integration-config.type/availability
   {:integration-config.service/exchange {}
    :integration-config.service/m365     {:integration-config/client-id     schemas/UUID-string
                                          :integration-config/tenant-id     schemas/UUID-string
                                          ;; client secret, stored encrypted with :integration-secret
                                          :integration-config/client-secret s/Str}}

   :integration-config.type/plan-review
   {:integration-config.service/digeplan {:integration-config/base-url schemas/URI}
    :integration-config.service/bluebeam {}}
   :integration-config.type/ecm
   {:integration-config.service/laserfiche-cloud
    (st/merge {:integration-config/base-url      schemas/URI
               :integration-config/token-url     schemas/URI
               :integration-config/client-id     s/Str
               :integration-config/client-secret s/Str
               :integration-config/private-key   s/Str}
              laserfiche-common)
    :integration-config.service/laserfiche-self-hosted
    (st/merge {:integration-config/base-url        schemas/URI
               :integration-config/client-id       s/Str
               :integration-config/client-secret   s/Str
               :integration-config/ecm-volume-name s/Str}
              laserfiche-common)}
   :integration-config.type/model-codes
   {:integration-config.service/icc-code-connect
    {:integration-config/base-url      schemas/URI
     :integration-config/client-id     s/Str
     :integration-config/client-secret s/Str}}})

(s/defschema IntegrationConfigBase
  "A map superset schema for all integration configs"
  (st/merge
    {:integration-config/type    (->> integration-configs-by-service
                                      keys
                                      (apply s/enum))
     :integration-config/service (->> integration-configs-by-service
                                      vals
                                      (mapcat keys)
                                      (apply s/enum))}
    (->> integration-configs-by-service
         vals
         (mapcat vals)
         (apply merge)
         st/optional-keys)
    ;; Make sure we have the more generic versions of the keys in this map
    (st/optional-keys {:integration-config/client-id s/Str
                       :integration-config/tenant-id s/Str})))

(def mandatory-keys
  (->> IntegrationConfigBase keys (filter keyword?)))

(defn is-config-type-fn [expected-type expected-service]
  (fn [{selected-type    :integration-config/type
        selected-service :integration-config/service}]
    (and (= expected-type selected-type)
         (= selected-service expected-service))))


(defn integration-config-schema-for
  ([type service]
   (->> (get-in integration-configs-by-service [type service])
        (integration-config-schema-for type service)))
  ([type service schema]
   (-> IntegrationConfigBase
       (st/select-keys mandatory-keys)
       (st/merge schema)
       (st/merge {:integration-config/type    (s/eq type)
                  :integration-config/service (s/eq service)}))))

(s/defschema IntegrationConfig
  (->> integration-configs-by-service
       (mapcat (fn [[type services]]
                 (->> services
                      (mapcat (fn [[service schema]]
                                [(is-config-type-fn type service)
                                 (integration-config-schema-for type service schema)])))))
       (apply s/conditional)))

(def integration-config-keys
  (-> IntegrationConfigBase st/required-keys keys))

(def ^{:arglists '([integration-config])} child-subscriptions
  (child/child-entities-of-type-fn :entity.type/integration-subscription))

(def type-supports-connection-check?
  #{:integration-config.type/ecm
    :integration-config.type/model-codes})

(def features-configured-in-integration-view
  #{:group.feature/backend-system-integration
    :group.feature/external-plan-review-integration
    :group.feature/enable-bluebeam
    :group.feature/availability-integration
    :group.feature/ecm-integration
    :group.feature/model-codes-integration})

(def plan-review-service-enum
  "Schema enum of valid provider services for plan-review integration.
   Dynamically derived from integration-configs-by-service to ensure consistency.
   Returns the un-namespaced service keyword, e.g. :bluebeam, :digeplan."
  (->> integration-configs-by-service
       :integration-config.type/plan-review
       keys
       (map (comp keyword name))
       (apply s/enum)))
